
import React, { useEffect, useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Media } from "../media/types";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MediaViewerProps {
  media: string | Media | Media[] | null;
  onClose: () => void;
  initialIndex?: number;
}

type TouchPoint = { x: number; y: number };

export const MediaViewer = ({ media, onClose, initialIndex = 0 }: MediaViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState<TouchPoint | null>(null);
  const [touchEnd, setTouchEnd] = useState<TouchPoint | null>(null);

  // Convert media to array format for consistent handling
  const mediaArray = React.useMemo(() => {
    if (!media) return [];
    if (typeof media === 'string') return [{ url: media }];
    if (Array.isArray(media)) return media;
    return [media];
  }, [media]);

  // Set the initial index when the component mounts or media changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, media]);

  // Function to ensure scrolling is enabled
  const enableScrolling = () => {
    document.body.style.removeProperty('overflow');
  };

  // Ensure scrolling is properly managed when the viewer is opened/closed
  useEffect(() => {
    if (!media) {
      // When media is closed, ensure scrolling is re-enabled
      enableScrolling();
    }

    return () => {
      // Always ensure scrolling is enabled when component unmounts
      enableScrolling();
    };
  }, [media]);

  // Apply multiple timeouts to ensure scrolling is enabled
  useEffect(() => {
    const timeouts = [50, 150, 300, 500, 1000].map(delay =>
      setTimeout(enableScrolling, delay)
    );

    return () => timeouts.forEach(clearTimeout);
  }, []);

  // Navigation functions
  const goToNext = useCallback(() => {
    if (mediaArray.length <= 1) return;
    setCurrentIndex(prevIndex =>
      prevIndex === mediaArray.length - 1 ? 0 : prevIndex + 1
    );
  }, [mediaArray.length]);

  const goToPrevious = useCallback(() => {
    if (mediaArray.length <= 1) return;
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? mediaArray.length - 1 : prevIndex - 1
    );
  }, [mediaArray.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!media || mediaArray.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [media, mediaArray.length, onClose, goToNext, goToPrevious]);

  // Touch handlers: horizontal swipe navigates, vertical swipe-down dismisses.
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchEnd.y - touchStart.y;

    // Vertical-dominant downward swipe dismisses. Threshold larger than horizontal
    // (80 vs 50) so accidental downward drift while scrolling photos doesn't close.
    if (Math.abs(deltaY) > Math.abs(deltaX) && deltaY > 80) {
      setTouchStart(null);
      setTouchEnd(null);
      onClose();
      return;
    }

    const isLeftSwipe = deltaX > 50;
    const isRightSwipe = deltaX < -50;

    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();

    setTouchStart(null);
    setTouchEnd(null);
  };

  if (!media || mediaArray.length === 0) return null;

  const currentMedia = mediaArray[currentIndex];
  const mediaUrl = currentMedia?.url || '';
  const streamId = (currentMedia as Media)?.stream_id;

  // If we couldn't extract a valid URL, don't render anything
  if (!mediaUrl && !streamId) return null;

  const mediaType = (currentMedia as Media)?.media_type;
  const isVideo = streamId || mediaType === 'video' || /\.(mp4|mov|quicktime|m4v|avi)$/i.test(mediaUrl);
  const fileExtension = mediaUrl.split('.').pop()?.toLowerCase();

  // Helper to determine the correct MIME type (for legacy videos without stream_id)
  const getVideoMimeType = (ext: string | undefined) => {
    switch (ext) {
      case 'mov':
      case 'quicktime':
        return 'video/quicktime';
      case 'm4v':
        return 'video/x-m4v';
      case 'avi':
        return 'video/x-msvideo';
      default:
        return 'video/mp4';
    }
  };

  // Only show arrows if there are multiple items
  const showNavigation = mediaArray.length > 1;

  return (
    <DialogPrimitive.Root
      open={!!media}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          // Ensure scrolling is enabled when dialog closes
          enableScrolling();
          // Apply multiple timeouts to ensure scrolling is enabled
          [50, 150, 300, 500].forEach(delay => {
            setTimeout(enableScrolling, delay);
          });
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[51] h-[100vh] w-[100vw] bg-black/90 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <DialogPrimitive.Title className="sr-only">Photo viewer</DialogPrimitive.Title>
          <div
            className="absolute inset-0 w-full h-full flex items-center justify-center"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {isVideo ? (
              streamId ? (
                <iframe
                  src={`https://iframe.videodelivery.net/${streamId}?autoplay=true`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              ) : (
                <video
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain"
                >
                  <source src={mediaUrl} type={getVideoMimeType(fileExtension)} />
                  <source src={mediaUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )
            ) : (
              <img
                src={mediaUrl}
                alt="Selected media"
                className="w-full h-full object-contain"
              />
            )}

            {/* Close button — respects iOS safe-area so it clears the notch/Dynamic Island */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute bg-black/50 hover:bg-black/70 h-11 w-11 rounded-full z-10"
              style={{
                top: 'max(0.5rem, calc(env(safe-area-inset-top) + 0.5rem))',
                right: 'max(0.5rem, calc(env(safe-area-inset-right) + 0.5rem))',
              }}
              onClick={onClose}
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              <span className="sr-only">Close</span>
            </Button>

            {showNavigation && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 h-8 w-8 sm:h-10 sm:w-10 rounded-full"
                  onClick={goToPrevious}
                >
                  <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                  <span className="sr-only">Previous</span>
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 h-8 w-8 sm:h-10 sm:w-10 rounded-full"
                  onClick={goToNext}
                >
                  <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                  <span className="sr-only">Next</span>
                </Button>

                {/* Position indicator */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm sm:text-base font-medium">
                  {currentIndex + 1} of {mediaArray.length}
                </div>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
